import React, {useState} from 'react';

import pickBy from 'lodash/pickBy';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {VALID_RANGE_TEMPLATE} from 'app/general/dates/date-range';
import {SelectDateRangeForm} from 'app/general/widgets/SelectDateRangeForm';
import {useAccountSettingsState} from 'app/settings/api';
import {taskDispatcher} from 'app/subscriptions/taskDispatcher';
import {EXPORT_ACTIVITY_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {postJson} from 'app/utils/request';

type StatusStateType = {start: string; end: string} & (
  | {
      status: 'idle' | 'processing' | 'error';
      downloadURL: string | void;
    }
  | {
      status: 'finished';
      downloadURL: string;
    }
);

export function DownloadAccountActivityForm(): JSX.Element {
  const {account_id} = useAccountSettingsState().data!;
  const actions = useAsyncActions({taskDispatcher});

  const [status, setStatus] = useState<StatusStateType>({
    status: 'idle',
    downloadURL: undefined,
    start: '',
    end: '',
  });

  function handleSubmit(ev: React.SyntheticEvent<HTMLFormElement>) {
    ev.preventDefault();
    setStatus({...status, status: 'processing'});

    return actions
      .taskDispatcher(
        postJson<unknown>(
          EXPORT_ACTIVITY_API_URL,
          pickBy({
            object_id: account_id,
            content_type: 'accountprofile',
            send_mail: true,
            datetime_start: status.start,
            datetime_end: status.end,
          })
        )
      )
      .then(
        ({result}) => {
          setStatus({
            ...status,
            status: 'finished',
            downloadURL: result,
          });
        },
        () =>
          setStatus({
            ...status,
            status: 'error',
          })
      );
  }

  return (
    <div className="pt-4">
      <h4 className="section pb-2 border-bottom">
        {gt('Export Company Activity')}
      </h4>
      <form onSubmit={handleSubmit}>
        <SelectDateRangeForm
          initialValue="this-month"
          onChange={({start, end}) =>
            setStatus({
              ...status,
              start,
              end,
            })
          }
          templates={availableTemplates}
          labelClass="col-xl-2 col-lg-3 col-md-3 col-sm-4"
          fieldWrapperClass="col-xl-3 col-lg-5 col-md-5 col-sm-6"
        />

        <div className="form-group row">
          <div className="col offset-xl-2 offset-lg-3 offset-md-3 offset-sm-4">
            <button
              disabled={status.start === '' || status.end === ''}
              className="btn btn-outline-primary activity-dl-js mb-5"
              type="submit"
            >
              {gt('Download activity')}
            </button>
          </div>
        </div>
      </form>
      <TabbedModal
        show={status.status !== 'idle'}
        onCancel={() =>
          setStatus({
            ...status,
            status: 'idle',
          })
        }
        tabs={[
          {
            eventKey: 'processing',
            name: gt('Company activity download'),
            icon: 'fas fa-download',
            content: (
              <div className="text-center">
                {status.status === 'processing' ? (
                  <div>
                    <p className="lead">
                      <i className="fa fa-circle-notch fa-spin" />{' '}
                      {gt('Gathering all account activity')}
                    </p>
                    <p>
                      {gt(
                        'This could take some minutes, your file will appear here and will be emailed to you when finished.'
                      )}
                    </p>
                  </div>
                ) : status.status === 'finished' ? (
                  <div>
                    <p className="lead">
                      {gt('Your activity data is ready for download.')}
                    </p>
                    <p>
                      <a
                        className="btn btn-primary download px-4"
                        href={status.downloadURL}
                      >
                        {gt('Download')}
                      </a>
                    </p>
                  </div>
                ) : status.status === 'error' ? (
                  <div className="text-danger">
                    <p className="lead">{gt("Couldn't create the file.")}</p>
                    <p>{gt('Please try again later')}.</p>
                  </div>
                ) : null}
                <StyledModalFooter>
                  <button
                    className="btn btn-link text-secondary"
                    onClick={() =>
                      setStatus({
                        ...status,
                        status: 'idle',
                      })
                    }
                  >
                    <i className="fa fa-times mr-1" />
                    {gt('Close')}
                  </button>
                </StyledModalFooter>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

const availableTemplates: Array<VALID_RANGE_TEMPLATE> = [
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'this-year',
  'custom',
];
